import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Stack,
    Typography,
    TextField,
    MenuItem,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    TableContainer,
    IconButton,
    Tooltip,
    CircularProgress,
    Snackbar,
    Alert,
} from "@mui/material";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import VendorFormDialog from "./VendorFormDialog";
import { api } from "../../../utils/api";

export default function VendorsPage() {
    const nav = useNavigate();

    const [loading, setLoading] = useState(false); // table loading
    const [centers, setCenters] = useState([]);
    const [rows, setRows] = useState([]);

    const [centerId, setCenterId] = useState("");
    const [search, setSearch] = useState("");

    const [open, setOpen] = useState(false);
    const [editRow, setEditRow] = useState(null);

    // ✅ Save & Upload UI states
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState("idle"); // idle | ok | fail

    // ✅ Snackbar
    const [snack, setSnack] = useState({
        open: false,
        severity: "success", // success | error | info
        message: "",
    });

    /* =========================
     * Fetch
     * ========================= */
    const fetchCenters = async () => {
        const res = await api.get("/centers");
        setCenters(res.data?.data || []);
    };

    const fetchVendors = async () => {
        setLoading(true);
        try {
            const params = {};
            if (centerId) params.center = centerId;

            const res = await api.get("/vendors", { params });
            setRows(res.data?.data || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCenters();
        fetchVendors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchVendors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [centerId]);

    /* =========================
     * Filter
     * ========================= */
    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter((r) => {
            const okCenter = centerId
                ? r.center?._id === centerId || r.center === centerId
                : true;
            const okSearch = q ? (r.name || "").toLowerCase().includes(q) : true;
            return okCenter && okSearch;
        });
    }, [rows, centerId, search]);

    /* =========================
     * Actions
     * ========================= */
    const handleAdd = () => {
        setEditRow(null);
        setUploadResult("idle");
        setOpen(true);
    };

    const handleEdit = (row, e) => {
        e.stopPropagation();
        setEditRow(row);
        setUploadResult("idle");
        setOpen(true);
    };

    const handleDelete = async (id, e) => {
        e.stopPropagation();
        if (!window.confirm("هل أنت متأكد من الحذف؟")) return;

        setLoading(true);
        try {
            await api.delete(`/vendors/${id}`);
            await fetchVendors();
            setSnack({ open: true, severity: "success", message: "تم الحذف بنجاح" });
        } catch (err) {
            setSnack({ open: true, severity: "error", message: "فشل الحذف" });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (payload) => {
        setSaving(true);
        setUploading(false);
        setUploadResult("idle");

        try {
            let savedId = editRow?._id;

            // ✅ 1) تنظيف payload (لا نرسل File داخل JSON)
            const cleanPayload = {
                ...payload,
                contract: { ...payload.contract },
            };
            delete cleanPayload.contract.file;

            // ✅ 2) حفظ البيانات الأساسية
            if (editRow?._id) {
                await api.put(`/vendors/${editRow._id}`, cleanPayload);
            } else {
                const res = await api.post("/vendors", cleanPayload);
                savedId = res.data?.data?._id;
            }

            // ✅ 3) رفع ملف العقد (إذا موجود)
            const file = payload?.contract?.file;
            if (file && savedId) {
                setUploading(true);
                try {
                    const fd = new FormData();
                    fd.append("contractFile", file);

                    await api.put(`/vendors/${savedId}/contract-file`, fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });

                    setUploadResult("ok");
                    setSnack({
                        open: true,
                        severity: "success",
                        message: editRow ? "تم التعديل + تم رفع العقد ✅" : "تمت الإضافة + تم رفع العقد ✅",
                    });
                } catch (err) {
                    setUploadResult("fail");
                    setSnack({
                        open: true,
                        severity: "error",
                        message: "تم الحفظ لكن فشل رفع ملف العقد ❌",
                    });
                } finally {
                    setUploading(false);
                }
            } else {
                // لا يوجد ملف
                setSnack({
                    open: true,
                    severity: "success",
                    message: editRow ? "تم التعديل بنجاح ✅" : "تمت الإضافة بنجاح ✅",
                });
            }

            setOpen(false);
            await fetchVendors();
        } catch (err) {
            setSnack({
                open: true,
                severity: "error",
                message: "فشل الحفظ ❌ (راجع السيرفر)",
            });
        } finally {
            setSaving(false);
        }
    };

    /* =========================
     * Render
     * ========================= */
    return (
        <Box p={2}>
            <Typography variant="h5" mb={2}>
                الشركات الخارجية
            </Typography>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <TextField
                        select
                        label="المركز"
                        value={centerId}
                        onChange={(e) => setCenterId(e.target.value)}
                        fullWidth
                    >
                        <MenuItem value="">كل المراكز</MenuItem>
                        {centers.map((c) => (
                            <MenuItem key={c._id} value={c._id}>
                                {c.name} ({c.code})
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="بحث بالاسم"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        fullWidth
                    />

                    <Button variant="outlined" onClick={fetchVendors}>
                        تحديث
                    </Button>

                    <Button variant="contained" startIcon={<AddCircleIcon />} onClick={handleAdd}>
                        إضافة
                    </Button>
                </Stack>
            </Paper>

            <TableContainer component={Paper}>
                {loading ? (
                    <Box p={4} textAlign="center">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>المركز</TableCell>
                                <TableCell>الاسم</TableCell>
                                <TableCell>النوع</TableCell>
                                <TableCell>العقد</TableCell>
                                <TableCell>المبلغ</TableCell>
                                <TableCell>الحالة</TableCell>
                                <TableCell align="right">إجراءات</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filteredRows.map((r) => (
                                <TableRow
                                    key={r._id}
                                    hover
                                    sx={{ cursor: "pointer" }}
                                    onClick={() => nav(`/vendors/${r._id}`)}
                                >
                                    <TableCell>
                                        {r.center?.name ? `${r.center.name} (${r.center.code})` : "-"}
                                    </TableCell>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell>{r.type || "-"}</TableCell>
                                    <TableCell>{r.contract?.pricingType || "-"}</TableCell>
                                    <TableCell>
                                        {r.contract?.amount ?? "-"} {r.contract?.currency || ""}
                                    </TableCell>
                                    <TableCell>{r.contract?.status || "-"}</TableCell>

                                    <TableCell align="right">
                                        <Tooltip title="تعديل">
                                            <IconButton onClick={(e) => handleEdit(r, e)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="حذف">
                                            <IconButton onClick={(e) => handleDelete(r._id, e)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}

                            {filteredRows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        لا توجد بيانات
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            {/* Dialog */}
            <VendorFormDialog
                open={open}
                onClose={() => setOpen(false)}
                onSubmit={handleSave}
                centers={centers}
                editRow={editRow}
                saving={saving}
                uploading={uploading}
                uploadResult={uploadResult}
            />

            {/* Snackbar */}
            <Snackbar
                open={snack.open}
                autoHideDuration={2500}
                onClose={() => setSnack((p) => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={snack.severity}
                    variant="filled"
                    onClose={() => setSnack((p) => ({ ...p, open: false }))}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
